import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { API } from '../api';
import { getToken, removeToken, saveToken } from '../auth';
import { setCompany, setLoggedIn, setTalentUser } from '../redux/actions';
import { withLanguage } from '../components/common/Texts';

const LOGIN_TIMEOUT_MS = 15000;

// 'aborted' is intentionally excluded: AbortErrors caused by a new submit or
// navigation are not real network failures and must not show an error message.
const NETWORK_ERROR_PATTERN = /timeout|failed to fetch|networkerror|load failed|network request failed/i;

function withTimeout(promise, ms, controller) {
    let timer;
    const timeout = new Promise((_, reject) => {
        timer = setTimeout(() => {
            controller?.abort();
            reject(new Error('timeout'));
        }, ms);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

function messageFor(err, t) {
    const status = err?.response?.status;
    if (status === 400 || status === 401) return t('failed_login');
    if (status === 403) return t('login_error_forbidden');
    if (NETWORK_ERROR_PATTERN.test(err?.message || '')) return t('login_error_network');
    return t('login_error_unexpected');
}

export default function useLogin() {
    const dispatch = useDispatch();
    const loggedIn = useSelector((state) => state.user.loggedIn);
    const language = useSelector((state) => state.user.language);
    const t = withLanguage(language);

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const controllerRef = useRef(null);

    useEffect(() => () => controllerRef.current?.abort(), []);

    const submit = async () => {
        const cleanUsername = username.trim();
        if (!cleanUsername || !password) {
            setError(t('failed_login'));
            return;
        }

        controllerRef.current?.abort();
        const controller = new AbortController();
        controllerRef.current = controller;

        setError('');
        setLoading(true);

        let savedToken = null;
        try {
            const { data } = await withTimeout(
                API.post('/api-token-auth/', { username: cleanUsername, password }, { signal: controller.signal }),
                LOGIN_TIMEOUT_MS,
                controller,
            );
            if (!data?.access) throw new Error('missing_access_token');
            saveToken(data.access);
            savedToken = data.access;

            const [company, talentUser] = await withTimeout(
                Promise.all([
                    API.get('/company/', { signal: controller.signal }),
                    API.get('/talentuser/', { signal: controller.signal }),
                ]),
                LOGIN_TIMEOUT_MS,
                controller,
            );

            dispatch(setCompany(company.data));
            // /talentuser/ devuelve el TalentUser con el User de Django anidado;
            // el perfil (is_staff incluido) vive en .user, no en la raíz.
            dispatch(setTalentUser(talentUser.data?.user));
            dispatch(setLoggedIn(cleanUsername));
        } catch (e) {
            // Always abort to cancel any orphan parallel request (e.g. the other
            // leg of Promise.all that didn't reject first).
            controller.abort();
            // Remove the token even if a newer submit replaced controllerRef,
            // but only if storage still holds the one this submit saved — a
            // newer successful submit may have already overwritten it.
            if (savedToken && getToken() === savedToken) removeToken();
            // Discard the visible error from requests that were superseded.
            if (controllerRef.current !== controller) return;
            setError(messageFor(e, t));
        } finally {
            // Only update loading state for the controller that is still active;
            // a newer submit should manage its own loading lifecycle.
            if (controllerRef.current === controller) {
                controllerRef.current = null;
                setLoading(false);
            }
        }
    };

    return {
        username, setUsername,
        password, setPassword,
        error, loading, loggedIn,
        submit,
    };
}
